import { Alert, AlertTitle, Typography } from '@cmg/design-system';
import pluralize from 'pluralize';
import React from 'react';

import type { IssuerNameWithStockSymbol } from '../../../../../graphql/__generated__';
import type { Rolodex_FirmDetail_AffectedLiveOfferingsQuery } from './graphql/__generated__';

const formatOffering = ({ issuerName, stockSymbol }: IssuerNameWithStockSymbol) =>
  stockSymbol ? `${issuerName} (${stockSymbol})` : issuerName;

type Props = {
  affectedFirms: Rolodex_FirmDetail_AffectedLiveOfferingsQuery['listLiveOfferingsAffectedByFirmHierarchyUnlink'];
  firmNames: Record<string, string | null | undefined>;
};

export const AffectedLiveOfferingsAlert: React.FC<Props> = ({ affectedFirms, firmNames }) => {
  // Firms in one hierarchy never share an offering, so per-firm counts don't overlap.
  const offeringsCount = React.useMemo(
    () =>
      affectedFirms.reduce(
        (count, { issuerNamesWithStockSymbols }) => count + issuerNamesWithStockSymbols.length,
        0
      ),
    [affectedFirms]
  );

  if (affectedFirms.length === 0) {
    return null;
  }

  return (
    <Alert severity="warning">
      <AlertTitle>
        {affectedFirms.length} affected {pluralize('firm', affectedFirms.length)}{' '}
        {pluralize('is', affectedFirms.length)} participating on on-platform{' '}
        {pluralize('offering', offeringsCount)}
      </AlertTitle>
      <Typography variant="body2">
        Saving this change removes access to {pluralize('offering', offeringsCount, true)} for these
        firms and all users in their tenants.
      </Typography>
      {affectedFirms.map(({ cmgEntityKey, issuerNamesWithStockSymbols }) => (
        <Typography key={cmgEntityKey} variant="body2">
          {firmNames[cmgEntityKey] ?? cmgEntityKey} —{' '}
          {issuerNamesWithStockSymbols.map(formatOffering).join(', ')}
        </Typography>
      ))}
    </Alert>
  );
};
